import { sortArray } from "@/utils/sort";

import type { EntityTreeNode } from "@waku-care/types/contracts/internal";

export type { EntityTreeNode, EntityTreeType } from "@waku-care/types/contracts/internal";

/**
 * A leveled tree node, mirroring the `PaginatedDeviceTagLeveled` shape: it carries the
 * hierarchical `level`, `value`/`label` for rendering, and nested `children`.
 */
export type EntityTreeNodeLeveled = EntityTreeNode & {
  value: string;
  label: string;
  level: number;
  children?: EntityTreeNodeLeveled[];
};

/**
 * Returns the human-readable label for a tree node: a case's `name` or a maintenance's `title`.
 */
const getEntityTreeNodeLabel = (node: EntityTreeNode): string => {
  return node.entity_type === "case" ? node.entity.name : node.entity.title;
};

/**
 * Turns the flat, descendant tree rows (backend `GET /cases/:id/tree`, `GET /maintenances/:id/tree`) into a
 * nested, leveled tree structure - mirroring `getDeviceTagsLeveled`.
 *
 * Each node is linked to its parent via `parent_id` (the analog of `parent_device_tag_id`); the direct children
 * of the queried entity have `parent_id === null`. A node with several parents is returned once per parent, so it
 * appears below each of them.
 */
export const getEntityTreeLeveled = (nodes: EntityTreeNode[]): EntityTreeNodeLeveled[] => {
  const childrenMap = nodes.reduce<Record<string, { node?: EntityTreeNode; children: EntityTreeNode[] }>>(
    (reduceObj, node) => {
      // oxlint-disable-next-line typescript/no-unnecessary-condition
      if (!reduceObj[node.entity_id]) {
        reduceObj[node.entity_id] = { node, children: [] };
      } else reduceObj[node.entity_id].node ??= node;
      if (node.parent_id) {
        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (!reduceObj[node.parent_id]) {
          reduceObj[node.parent_id] = { node: undefined, children: [] };
        }
        reduceObj[node.parent_id].children = [...reduceObj[node.parent_id].children, node];
      }
      return reduceObj;
    },
    {},
  );

  const getChildren = ({
    children,
    level,
  }: {
    children: EntityTreeNode[];
    level: number;
  }): EntityTreeNodeLeveled[] | undefined => {
    if (children.length <= 0) return undefined;
    return children.map((child) => {
      const newLevel = level + 1;
      const grandChildren = getChildren({ children: childrenMap[child.entity_id].children, level: newLevel });
      return {
        ...child,
        value: child.entity_id,
        label: getEntityTreeNodeLabel(child),
        level: newLevel,
        children: grandChildren,
      };
    });
  };

  const tree: EntityTreeNodeLeveled[] = [];
  for (const entity_id in childrenMap) {
    const node = childrenMap[entity_id].node;
    if (node && node.parent_id === null) {
      const level = 1;
      const children = getChildren({ children: childrenMap[entity_id].children, level });
      tree.push({ ...node, value: node.entity_id, label: getEntityTreeNodeLabel(node), level, children });
    }
  }

  return sortArray(tree).asc("label");
};

export const isTreeNodeCase = (node: EntityTreeNode): boolean => {
  return node.entity_type === "case";
};

export const isTreeNodeMaintenance = (node: EntityTreeNode): boolean => {
  return node.entity_type === "maintenance";
};
